// Overlay de CONTRÔLES TACTILES (mobile uniquement) : un joystick virtuel (pouce gauche, déplacement
// 4 directions) + un bouton d'action contextuel (pouce droit : « Parler » face à un Kazi, « Fermer »
// pendant un dialogue, estompé sinon). Lancé une seule fois par GameScene.create() UNIQUEMENT sur
// appareil tactile (cf. utils/touch). Caméra zoom 1, scène persistante (ne dort jamais) qui pilote la
// scène de JEU active — réutilise exactement les mêmes appels que le clavier (gridEngine.move + bus).
//
// Joystick = implémentation MAISON sur coordonnées-écran brutes (pointer.x/y), pas le plugin rex :
// activation tolérante (toute la zone bas-gauche), base qui suit le pouce, détection de direction
// fiable sans aucun calcul caméra/monde (le plugin rex échouait sur mobile à cause de ça).
import 'phaser'
import { Direction } from 'grid-engine'
import { SceneName } from './enums/SceneNames'
import GlobalInfo from '../GlobalInfo'
import type GameScene from './GameScene'
import type { KaziPayload } from './DialogueScene'

type ActionState = 'idle' | 'talk' | 'next'

const JOY_RADIUS = 56   // déplacement max du pouce depuis le centre de la base
const JOY_DEADZONE = 18 // distance min (px) avant qu'une direction soit prise en compte
const BTN_RADIUS = 40
const MARGIN = 78 // marge depuis les bords de l'écran (safe-area pouce + home indicator)

export default class ControlsScene extends Phaser.Scene {
    private joyBase!: Phaser.GameObjects.Arc
    private joyThumb!: Phaser.GameObjects.Arc
    private joyHome = { x: 0, y: 0 }
    private joyPointerId: number | null = null
    private joyDir = { up: false, down: false, left: false, right: false }

    private actionBg!: Phaser.GameObjects.Arc
    private actionLabel!: Phaser.GameObjects.Text
    private actionState: ActionState = 'idle'
    private pendingKazi: KaziPayload | null = null

    constructor() {
        super(SceneName.Controls)
    }

    create(): void {
        // --- Joystick (pouce gauche) : anneau de base contrasté + pouce clair.
        this.joyBase = this.add.circle(0, 0, JOY_RADIUS, 0x1a202c, 0.45).setStrokeStyle(4, 0xffffff, 0.85)
        this.joyThumb = this.add.circle(0, 0, JOY_RADIUS * 0.45, 0xffffff, 0.9).setStrokeStyle(3, 0x10151f, 0.7)

        // Plusieurs pointeurs simultanés : joystick + bouton d'action en même temps.
        this.input.addPointer(2)
        this.input.on('pointerdown', this.onJoyDown, this)
        this.input.on('pointermove', this.onJoyMove, this)
        this.input.on('pointerup', this.onJoyUp, this)
        this.input.on('pointerupoutside', this.onJoyUp, this)

        // --- Bouton d'action (pouce droit) : cercle interactif + libellé contextuel.
        this.actionBg = this.add.circle(0, 0, BTN_RADIUS, 0x90cdf4, 0.95)
            .setStrokeStyle(3, 0xffffff, 0.9)
            .setInteractive({ useHandCursor: true })
        this.actionLabel = this.add.text(0, 0, '', {
            fontSize: '14px', color: '#10151f', fontStyle: 'bold',
        }).setOrigin(0.5)
        this.actionBg.on('pointerdown', this.onActionTap, this)

        this.layoutControls()
        this.actionState = 'idle' // force un premier refresh du libellé
        this.refreshActionButton(GlobalInfo._gameProgress.inDialogue)

        // Pendant un dialogue : masque le joystick et relâche la saisie en cours (pas de marche bloquée) ;
        // le bouton d'action reste, en mode « Fermer ».
        const onDialogue = (v: boolean): void => {
            this.joyBase.setVisible(!v)
            this.joyThumb.setVisible(!v)
            if (v) this.releaseJoystick()
            this.publishZones()
        }
        GlobalInfo.on('inDialogue', onDialogue)

        this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
            GlobalInfo.off('inDialogue', onDialogue)
            this.input.off('pointerdown', this.onJoyDown, this)
            this.input.off('pointermove', this.onJoyMove, this)
            this.input.off('pointerup', this.onJoyUp, this)
            this.input.off('pointerupoutside', this.onJoyUp, this)
            this.scale.off('resize', this.layoutControls, this)
            GlobalInfo._gameProgress.controlZones = undefined
        })

        // En mode Scale.FIT la taille logique reste 600×600, mais on repositionne par sécurité.
        this.scale.on('resize', this.layoutControls, this)
    }

    update(): void {
        const inDlg = GlobalInfo._gameProgress.inDialogue
        let dir: Direction | null = null
        if (!inDlg) {
            // Même priorité que manualMovement (Characters.ts) : haut > gauche > bas > droite.
            if (this.joyDir.up) dir = Direction.UP
            else if (this.joyDir.left) dir = Direction.LEFT
            else if (this.joyDir.down) dir = Direction.DOWN
            else if (this.joyDir.right) dir = Direction.RIGHT
            const s = this.getActiveScene()
            if (dir && s && s.gridEngine.hasCharacter(s.playerName)) {
                s.gridEngine.move(s.playerName, dir)
            }
        }
        // Publie l'intention de direction du joystick : NpcAndObjects s'en sert pour déclencher les
        // PORTES (le clavier lisait cursors.isDown, que le joystick ne met pas à jour).
        GlobalInfo._gameProgress.moveDir = dir
        this.refreshActionButton(inDlg)
    }

    // --- Joystick : entrées tactiles en coordonnées-écran ---

    private onJoyDown(pointer: Phaser.Input.Pointer): void {
        if (GlobalInfo._gameProgress.inDialogue) return
        if (this.joyPointerId !== null) return // déjà un pouce sur le joystick
        const W = this.scale.width, H = this.scale.height
        // Zone d'activation = bas-gauche de l'écran (le bouton d'action est en bas-droite).
        if (!(pointer.x < W * 0.5 && pointer.y > H * 0.45)) return

        this.joyPointerId = pointer.id
        // La base saute sous le pouce (bornée pour rester entièrement à l'écran).
        const cx = Phaser.Math.Clamp(pointer.x, JOY_RADIUS, W - JOY_RADIUS)
        const cy = Phaser.Math.Clamp(pointer.y, JOY_RADIUS, H - JOY_RADIUS)
        this.joyBase.setPosition(cx, cy).setAlpha(0.6)
        this.joyThumb.setPosition(cx, cy).setAlpha(1)
        this.updateJoyDir(0, 0)
        this.publishZones()
    }

    private onJoyMove(pointer: Phaser.Input.Pointer): void {
        if (pointer.id !== this.joyPointerId) return
        let dx = pointer.x - this.joyBase.x
        let dy = pointer.y - this.joyBase.y
        const dist = Math.hypot(dx, dy)
        if (dist > JOY_RADIUS) { // borne le pouce au rayon de la base
            dx = (dx / dist) * JOY_RADIUS
            dy = (dy / dist) * JOY_RADIUS
        }
        this.joyThumb.setPosition(this.joyBase.x + dx, this.joyBase.y + dy)
        this.updateJoyDir(dx, dy)
    }

    private onJoyUp(pointer: Phaser.Input.Pointer): void {
        if (pointer.id !== this.joyPointerId) return
        this.releaseJoystick()
    }

    // Direction 4 voies par axe dominant (zone morte au centre).
    private updateJoyDir(dx: number, dy: number): void {
        const d = this.joyDir
        d.up = d.down = d.left = d.right = false
        if (Math.hypot(dx, dy) < JOY_DEADZONE) return
        if (Math.abs(dx) > Math.abs(dy)) {
            if (dx < 0) d.left = true; else d.right = true
        } else {
            if (dy < 0) d.up = true; else d.down = true
        }
    }

    // Relâche le joystick : retour à la position de repos, pouce recentré, plus aucune direction.
    private releaseJoystick(): void {
        this.joyPointerId = null
        this.joyDir.up = this.joyDir.down = this.joyDir.left = this.joyDir.right = false
        this.joyBase.setPosition(this.joyHome.x, this.joyHome.y).setAlpha(0.45)
        this.joyThumb.setPosition(this.joyHome.x, this.joyHome.y).setAlpha(0.9)
    }

    // Place le repos du joystick (bas-gauche) et le bouton (bas-droite), puis publie leurs zones.
    private layoutControls(): void {
        const W = this.scale.width, H = this.scale.height
        this.joyHome = { x: MARGIN + JOY_RADIUS, y: H - MARGIN - JOY_RADIUS }
        if (this.joyPointerId === null) {
            this.joyBase.setPosition(this.joyHome.x, this.joyHome.y)
            this.joyThumb.setPosition(this.joyHome.x, this.joyHome.y)
        }
        const bx = W - MARGIN - BTN_RADIUS, by = H - MARGIN - BTN_RADIUS
        this.actionBg.setPosition(bx, by)
        this.actionLabel.setPosition(bx, by)
        this.publishZones()
    }

    // Publie les zones-écran des contrôles (rectangles, coords canvas partagées entre scènes) pour
    // que GameScene.pointerdown n'ouvre pas une fiche Kazi quand on touche un contrôle.
    private publishZones(): void {
        const W = this.scale.width, H = this.scale.height
        const zones: Array<{ x: number; y: number; w: number; h: number }> = [
            // bouton d'action (carré englobant le cercle)
            { x: this.actionBg.x - BTN_RADIUS, y: this.actionBg.y - BTN_RADIUS, w: BTN_RADIUS * 2, h: BTN_RADIUS * 2 },
        ]
        if (this.joyBase.visible) {
            // zone d'activation du joystick = tout le bas-gauche (cf. onJoyDown)
            zones.push({ x: 0, y: H * 0.45, w: W * 0.5, h: H * 0.55 })
        }
        GlobalInfo._gameProgress.controlZones = zones
    }

    // Recalcule l'état/libellé du bouton chaque frame (ne touche au DOM que si l'état change).
    private refreshActionButton(inDlg: boolean): void {
        let next: ActionState = 'idle'
        let kazi: KaziPayload | null = null
        if (inDlg) {
            next = 'next'
        } else {
            const s = this.getActiveScene()
            if (s && s.gridEngine.hasCharacter(s.playerName)) {
                const fp = s.gridEngine.getFacingPosition(s.playerName)
                const reg = (s as any).kaziByTile as Record<string, any> | undefined
                const k = reg && reg[`${fp.x},${fp.y}`]
                if (k) {
                    next = 'talk'
                    kazi = { id: k.id, displayName: k.displayName, role: k.role, dialogueHook: k.dialogueHook }
                }
            }
        }
        this.pendingKazi = kazi
        if (next === this.actionState) return
        this.actionState = next
        this.actionLabel.setText(next === 'next' ? 'Fermer' : next === 'talk' ? 'Parler' : '·')
        const active = next !== 'idle'
        this.actionBg.setAlpha(active ? 0.95 : 0.35)
        this.actionLabel.setAlpha(active ? 1 : 0.5)
    }

    // Tap sur le bouton = équivalent de la touche E : avance/ferme le dialogue, ou ouvre la fiche
    // du Kazi en face. Réutilise les mêmes events que le relais clavier de GameScene.
    private onActionTap(): void {
        if (GlobalInfo._gameProgress.inDialogue) {
            GlobalInfo.emit('dialogue-advance')
            return
        }
        if (this.actionState === 'talk' && this.pendingKazi) {
            GlobalInfo.emit('show-kazi', this.pendingKazi)
        }
    }

    // Scène de jeu actuellement éveillée (publiée par GameScene sur create + WAKE).
    private getActiveScene(): GameScene | null {
        const name = GlobalInfo._gameProgress.activeScene as string
        if (!name) return null
        const s = this.scene.get(name) as unknown as GameScene
        return s && this.scene.isActive(name) ? s : null
    }
}
